<template>
    <div>
        <form-row title="WhatsApp Number" :label_align_top="true">
            <div class="w-full">
                <div class="flex flex-col lg:flex-row items-start gap-4 w-full">
                    <div class="w-full lg:w-2/5 flex-none">
                        <input-text name="whats_app_number" type="tel" :value="whatsAppNumber" @input="handleWhatsAppNumberInput" />
                        <p class="text-xs text-gray-500 mt-2 mb-0">Include the country code, for example +1 520 555 0123.</p>
                    </div>
                    <team-message-consent v-if="showTeamMessageConsent" channel="whatsapp" :number="whatsAppNumber" :value="!!broker.team_whatsapp_consent_at" />
                </div>
                <input-radio-boolean
                    v-if="communityEnabled && hasWhatsAppNumber"
                    :key="`community-${inWhatsAppCommunity}`"
                    name="in_whats_app_community"
                    label="Join the C2C WhatsApp Community?"
                    :value="inWhatsAppCommunity ? 1 : 0"
                    true="Yes" false="No" class="mt-4"
                    @input="handleCommunityChange"
                />
            </div>
        </form-row>

        <whatsapp-community-modal
            v-if="communityEnabled"
            :show="showModal"
            @accepted="handleWhatsAppCommunityResponse"
            @closed="showModal = false"
        />
    </div>
</template>

<script>
import TeamMessageConsent from './team-message-consent.vue';
import WhatsappCommunityModal from '../../../../components/whatsapp-community-modal.vue';

export default {
    components: {
        WhatsappCommunityModal,
        TeamMessageConsent,
    },

    props: {
        showTeamMessageConsent: { type: Boolean, default: false },
        communityEnabled: { type: Boolean, default: false },
        broker: { default: () => ({}) },
        previousWhatsAppNumber: { default: null },
    },

    data() {
        // Normalize empty strings to null for initial values
        const initialValue = this.broker.whats_app_number;
        const normalizedInitial = (initialValue && String(initialValue).trim() !== '') ? initialValue : null;
        
        return {
            whatsAppNumber: normalizedInitial,
            initialWhatsAppNumber: normalizedInitial,
            inWhatsAppCommunity: this.broker.in_whats_app_community || false,
            modalShownForCurrentNumber: false,
            userHasInteractedWithRadio: false,
            showModal: false,
        }
    },

    computed: {
        hasWhatsAppNumber() {
            return this.whatsAppNumber && this.whatsAppNumber.toString().trim() !== '';
        },
    },

    mounted() {
        // Ensure initialWhatsAppNumber is set correctly on mount
        // Normalize empty strings to null
        const initialValue = this.broker.whats_app_number;
        this.initialWhatsAppNumber = (initialValue && String(initialValue).trim() !== '') ? initialValue : null;
    },

    watch: {
        'broker.in_whats_app_community'(newValue) {
            // Convert to boolean and update local state
            this.inWhatsAppCommunity = Boolean(newValue);
        },
        'broker.whats_app_number'(newValue) {
            // Update local value when broker prop changes (e.g., after save)
            const oldValue = this.whatsAppNumber;
            const newValueStr = newValue ? String(newValue).trim() : '';
            const oldValueStr = oldValue ? String(oldValue).trim() : '';
            
            this.whatsAppNumber = newValue || null;
            
            // Only reset flags if the value actually changed
            if (newValueStr !== oldValueStr) {
                // If number was cleared, reset interaction flag
                if (!newValueStr) {
                    this.userHasInteractedWithRadio = false;
                    this.modalShownForCurrentNumber = false;
                }
                // Don't reset flags if number was added - let checkAndShowModal handle that
            }
        },
    },

    methods: {
        handleWhatsAppNumberInput(value) {
            // Update local value as user types
            this.whatsAppNumber = value;
        },
        checkAndShowModal(newWhatsAppNumber) {
            if (!this.communityEnabled) return;
            // Safely check if there was a number initially (when component was created)
            const initialValueStr = this.initialWhatsAppNumber ? String(this.initialWhatsAppNumber).trim() : '';
            const hadNumberInitially = initialValueStr !== '';
            
            // Check both the response value and the local value (what user typed)
            const responseValueStr = newWhatsAppNumber ? String(newWhatsAppNumber).trim() : '';
            const localValueStr = this.whatsAppNumber ? String(this.whatsAppNumber).trim() : '';
            
            const responseHasNumber = responseValueStr !== '';
            const localHasNumber = localValueStr !== '';
            
            // Use whichever indicates a number is present
            const hasNumberNow = responseHasNumber || localHasNumber;

            // Check if this is a new number being added (was empty initially, now has value)
            const isNewNumber = !hadNumberInitially && hasNumberNow;
            
            if (!isNewNumber) {
                return;
            }

            // Don't show modal if user has already selected "Yes" (inWhatsAppCommunity is true)
            if (this.inWhatsAppCommunity) {
                return;
            }

            // Show modal if:
            // - New number was added
            // - User hasn't already selected "Yes" (checked above)
            // - User hasn't interacted with the radio button in this session
            // - We haven't already shown the modal for this number
            if (!this.userHasInteractedWithRadio && !this.modalShownForCurrentNumber) {
                this.modalShownForCurrentNumber = true;
                this.showModal = true;
            }
        },
        handleWhatsAppCommunityResponse(accepted) {
            // Update local state immediately so radio button updates right away
            this.inWhatsAppCommunity = Boolean(accepted);
            // Mark that user has made a choice via modal
            this.userHasInteractedWithRadio = true;
            this.modalShownForCurrentNumber = true;
            // Hide the modal
            this.showModal = false;
            // Emit event to parent to update in_whats_app_community
            this.$emit('community-response', accepted);
        },
        handleCommunityChange(value) {
            // Update local state when radio button changes
            this.inWhatsAppCommunity = value === 1;
            // Mark that user has interacted with the radio button
            this.userHasInteractedWithRadio = true;
            this.modalShownForCurrentNumber = true;
        },
    },
}
</script>

